import { t } from '../i18n';
import { esriTileUrl, project, type TileCoord } from './slippyMap';

/**
 * Satellite imagery sources for the hole viewer.
 *
 * The two available sources work in fundamentally different ways, so this is a tagged union
 * rather than a single "give me a URL" interface:
 *
 * **Esri World Imagery** — a *tile* service. A grid of 256px images covering a bounding box,
 * at any resolution, with no key and no billing. Attribution must be drawn by us.
 *
 * **Google Maps Static API** — a *static* service. One image for a given centre, zoom and
 * size. No session handshake, just a key; the Google logo and copyright are burned into the
 * returned image, so nothing needs drawing. The catch is a hard size ceiling — 640×640
 * logical px on the standard tier, doubled in real pixels via `scale=2` — which caps how
 * sharp a large stage can get.
 *
 * The undocumented `mt{0-3}.google.com/vt?lyrs=s` endpoints return the same imagery with no
 * key at all and are not used: they break Google's Terms of Service.
 */

export type SourceId = 'esri' | 'google';

export interface StaticRequest {
  centerLat: number;
  centerLng: number;
  zoom: number;
  /** Logical pixels — what the API's `size` parameter takes. */
  width: number;
  height: number;
}

interface CommonSource {
  id: SourceId;
  label: string;
  /** Empty when the provider burns attribution into the image itself. */
  attribution: string;
}

export type ImagerySource =
  | (CommonSource & { kind: 'tiles'; tileUrl: (tile: TileCoord) => string })
  | (CommonSource & {
      kind: 'static';
      staticUrl: (req: StaticRequest) => string;
      /** Largest logical width or height the plan allows. */
      maxLogicalSize: number;
      /** Device-pixel multiplier the request asks for. */
      scale: number;
    });

const ESRI: ImagerySource = {
  id: 'esri',
  kind: 'tiles',
  label: 'Esri World Imagery',
  attribution: t('hole.imageryEsri'),
  tileUrl: esriTileUrl,
};

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
const CONFIGURED = (import.meta.env.VITE_MAP_PROVIDER as SourceId | undefined) ?? 'esri';

/**
 * 640 is the standard-tier ceiling for `size`. Premium accounts may raise it, so it is an
 * env var rather than a constant — moving plans should not need a code change.
 */
const GOOGLE_MAX_LOGICAL =
  Number(import.meta.env.VITE_GOOGLE_STATIC_MAX_SIZE as string | undefined) || 640;

const GOOGLE_STATIC: ImagerySource = {
  id: 'google',
  kind: 'static',
  label: 'Google Satellite',
  // The Static API stamps its own logo and copyright into the image.
  attribution: '',
  maxLogicalSize: GOOGLE_MAX_LOGICAL,
  scale: 2,
  staticUrl: ({ centerLat, centerLng, zoom, width, height }) => {
    const params = new URLSearchParams({
      center: `${centerLat},${centerLng}`,
      zoom: String(zoom),
      size: `${Math.round(width)}x${Math.round(height)}`,
      scale: '2',
      maptype: 'satellite',
      format: 'jpg',
      key: GOOGLE_API_KEY ?? '',
    });
    return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
  },
};

export interface SourceResolution {
  source: ImagerySource;
  /** Set when the configured source could not be used, so the UI can say why. */
  fellBackBecause: string | null;
}

/**
 * The source to draw with. Never throws: imagery failing is a degraded view, not a broken
 * screen, and the viewer always has the schematic to fall back to.
 */
export function resolveImagerySource(): SourceResolution {
  if (CONFIGURED !== 'google') return { source: ESRI, fellBackBecause: null };

  if (!GOOGLE_API_KEY) {
    return { source: ESRI, fellBackBecause: t('hole.imageryNoKey') };
  }

  return { source: GOOGLE_STATIC, fellBackBecause: null };
}

const TILE = 256;

/** Mercator Y in the 0..1 range, the form the tile maths uses. */
function mercatorY(lat: number): number {
  const clamped = Math.max(-85.0511, Math.min(85.0511, lat));
  const rad = (clamped * Math.PI) / 180;
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2;
}

/**
 * The largest **integer** zoom at which a bounding box still fits a logical viewport.
 *
 * Static Maps takes an integer zoom, so the fit is chosen rather than computed: each step up
 * doubles the scale, so take the last one that still contains the whole hole. Latitude is
 * measured in projected units, not degrees — Mercator stretches towards the poles, and a
 * degree-based estimate would overshoot the zoom and clip the hole at high latitudes.
 */
export function zoomToFit(
  box: { minLongitude: number; maxLongitude: number; minLatitude: number; maxLatitude: number },
  logicalWidth: number,
  logicalHeight: number,
): number {
  const lngSpan = Math.max(box.maxLongitude - box.minLongitude, 1e-9);
  const widthAtZ0 = (lngSpan / 360) * TILE;

  const ySpan = Math.max(Math.abs(mercatorY(box.minLatitude) - mercatorY(box.maxLatitude)), 1e-12);
  const heightAtZ0 = ySpan * TILE;

  const zoomForWidth = Math.log2(logicalWidth / widthAtZ0);
  const zoomForHeight = Math.log2(logicalHeight / heightAtZ0);

  return Math.max(0, Math.min(21, Math.floor(Math.min(zoomForWidth, zoomForHeight))));
}

export interface GeoBox {
  minLongitude: number;
  maxLongitude: number;
  minLatitude: number;
  maxLatitude: number;
}

export interface StaticPlacement {
  /** What to ask the API for. */
  request: StaticRequest;
  /** Screen pixels per world pixel — the overlay uses the same factor. */
  displayScale: number;
  /** World-pixel coordinates that land at the centre of the stage. */
  originX: number;
  originY: number;
  /** Where the returned image sits on the stage. */
  imageBox: { left: number; top: number; width: number; height: number };
}

/**
 * The slack left around the hole, as a fraction of its bounding box.
 *
 * Deliberately much smaller than the tile path's 12%. Tiles scale continuously, so asking for
 * 12% yields exactly 12%. Static zoom is an integer, so each step doubles the scale: ask for
 * too much slack and a hole that would *just* have fitted the closer zoom drops to the next
 * one out and ends up at half the size, with the rest of the frame spent on the neighbouring
 * fairway. Quantisation already hands out slack for free — this is only the floor, and the
 * hole usually gets a good deal more.
 */
const STATIC_MARGIN = 0.06;

/**
 * Everything needed to draw one static image and put geometry on top of it.
 *
 * This is separated from the component because it is the one place where a wrong factor is
 * invisible until you look at a real photograph: the request and the overlay transform have
 * to be derived from the *same* logical size, or the hole drifts off the imagery. Tests can
 * assert that without an API key; a screenshot cannot be taken without one.
 *
 * `box` is the hole's own bounding box, unpadded — the margin is this function's business,
 * for the reason above. The request keeps the stage's aspect ratio so the image fills the
 * stage rather than being letterboxed, capped at the plan's ceiling on **both** axes: the API
 * rejects an oversized `size` outright instead of returning a smaller picture.
 */
export function planStaticImage(
  box: GeoBox,
  stageWidth: number,
  stageHeight: number,
  maxLogicalSize: number,
): StaticPlacement {
  const aspect = stageWidth / stageHeight;
  const logicalWidth = Math.round(aspect >= 1 ? maxLogicalSize : maxLogicalSize * aspect);
  const logicalHeight = Math.round(aspect >= 1 ? maxLogicalSize / aspect : maxLogicalSize);

  const centerLng = (box.minLongitude + box.maxLongitude) / 2;
  const centerLat = (box.minLatitude + box.maxLatitude) / 2;

  const halfLng = ((box.maxLongitude - box.minLongitude) / 2) * (1 + STATIC_MARGIN) || 1e-5;
  const halfLat = ((box.maxLatitude - box.minLatitude) / 2) * (1 + STATIC_MARGIN) || 1e-5;
  const zoom = zoomToFit(
    {
      minLongitude: centerLng - halfLng,
      maxLongitude: centerLng + halfLng,
      minLatitude: centerLat - halfLat,
      maxLatitude: centerLat + halfLat,
    },
    logicalWidth,
    logicalHeight,
  );

  // Via `project`, not the local `mercatorY`: the overlay draws with `projectGeo`, and the
  // two must be the same projection or the geometry sits next to the hole instead of on it.
  const { x: originX, y: originY } = project(centerLat, centerLng, zoom);

  // `min`, so the rounding above letterboxes by a pixel rather than clipping by one.
  const displayScale = Math.min(stageWidth / logicalWidth, stageHeight / logicalHeight);
  const width = logicalWidth * displayScale;
  const height = logicalHeight * displayScale;

  return {
    request: { centerLat, centerLng, zoom, width: logicalWidth, height: logicalHeight },
    displayScale,
    originX,
    originY,
    imageBox: {
      left: (stageWidth - width) / 2,
      top: (stageHeight - height) / 2,
      width,
      height,
    },
  };
}

export const configuredSource = CONFIGURED;
